import type { ApprovalMethod } from "@entrilia/shared/api/workflowTypes";
import type { Approval } from "../../api/types/approvalTypes";
import { pageRoutes } from "../../routes";

const isMembersChangeRequestApproval = (approval: Approval): boolean =>
  approval.itemType === "MembersChangeRequest" && !!approval.workflowId;

export const getApprovalNavigationPath = (approval: Approval): string | undefined => {
  const { clientCode } = approval;

  if (isMembersChangeRequestApproval(approval)) {
    return `/${clientCode}/${pageRoutes.workflows}/${pageRoutes.approvals}/${pageRoutes.changeRequest}/${approval.workflowId}`;
  }

  if (approval.objectId && approval.objectType) {
    return `/${clientCode}/${pageRoutes.workflows}/${pageRoutes.approvals}/${pageRoutes.approvalObject}/${approval.objectType}/${approval.objectId}`;
  }

  return undefined;
};

export const getApprovalProgress = (approval: Approval): string => {
  const stepsCount = approval.approvalStepStates.filter((step) => step.status !== "Skipped").length;

  const completedStepsCount = approval.approvalStepStates.filter(
    (step) => step.status !== "Skipped" && step.status !== "Pending"
  ).length;

  return stepsCount === 0 ? "-" : `${completedStepsCount}/${stepsCount}`;
};

// Names what is still outstanding, because the chain may have advanced or the same step may still be collecting approvals.
export const getPendingApprovalSuccessMessage = (approval: Approval): string => {
  const steps = approval.approvalStepStates.filter((step) => step.status !== "Skipped");
  const pendingStepIndex = steps.findIndex((step) => step.stepName === approval.currentApprovalStep);

  if (!approval.currentApprovalStep || pendingStepIndex < 0) {
    return "Your approval was recorded. This request is still pending approval";
  }

  return steps.length > 1
    ? `Your approval was recorded. Still pending: ${approval.currentApprovalStep} (step ${pendingStepIndex + 1} of ${steps.length})`
    : `Your approval was recorded. Still pending: ${approval.currentApprovalStep}`;
};

export const getApprovalMethodDescription = (method: ApprovalMethod) => {
  if (method.requireAll) {
    return "Everyone must approve";
  }

  if (method.minimumApprovals === 1) {
    return "Anyone can approve";
  }

  return `At least ${method.minimumApprovals} users must approve`;
};
